/**
 * Relative timestamps for the editorial card meta line
 * ("Size 140 · VV Zwolle · 2 hours ago").
 *
 * Copy lives under `common.relativeTime.*` in en.json / nl.json. Anything
 * older than a fortnight falls back to the locale's short date.
 */

/** Minimal shape of the i18next `t` returned by `useTranslation()`. */
export type TranslateFn = (key: string, options?: { count?: number }) => string;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const formatRelativeTime = (iso: string, t: TranslateFn): string => {
  const date = new Date(iso);
  const time = date.getTime();
  if (Number.isNaN(time)) return '';

  const diff = Date.now() - time;
  // Future timestamps (clock skew) read as "just now" rather than a negative count.
  if (diff < MINUTE) return t('common.relativeTime.justNow');
  if (diff < HOUR) return t('common.relativeTime.minutesAgo', { count: Math.floor(diff / MINUTE) });
  if (diff < DAY) return t('common.relativeTime.hoursAgo', { count: Math.floor(diff / HOUR) });
  if (diff < 2 * DAY) return t('common.relativeTime.yesterday');
  if (diff < 7 * DAY) return t('common.relativeTime.daysAgo', { count: Math.floor(diff / DAY) });
  if (diff < 14 * DAY) return t('common.relativeTime.lastWeek');

  return date.toLocaleDateString();
};
